import React, {Component, ReactNode} from 'react'
import AdminGeneralMeetingRepo, {
    GetGeneralMeetingInfoResponse,
    GetQuestionSummaryResponse,
    GetVotingPreferenceSummaryResponse
} from './AdminGeneralMeetingRepo'
import EventEmitterExAdmin from '../shared/pageBaseComponents/EventEmitterExAdmin'
import {AdminPagePropsSet, createAdminPage} from '../shared/hoc/createAdminPage'
import {HeadingAreaMessageParams} from '../shared/pageBaseComponents/HeadingAreaMessageComponent'
import {
    AdminDefaultButton,
    AdminPrimaryButton,
    AdminPrimaryOutlinedButton
} from '../../styledComponents/adminPage/Buttons'
import {BackLink, CancelLink, DeleteLink, StyledLink} from '../../styledComponents/adminPage/Links'
import {Nullable} from '../../app/TypeHelpers'
import {getFileExtension, judgeAuthorityCheck} from '../../__NoAutomatedTests/common/util/Natutil'
import {
    ColumnFlexContainer,
    FlexContainer,
    FullWidthColumnFlexContainer,
    FullWidthLeftColumnFlexContainer,
    FullWidthLeftRowFlexContainer,
    FullWidthRightRowFlexContainer,
    FullWidthRowFlexContainer,
    LeftColumnFlexContainer,
    LeftRowFlexContainer,
    LeftTopRowFlexContainer,
    RowFlexContainer
} from '../../styledComponents/FlexComponents'
import styled from 'styled-components'
import {Box, Divider, Paper, Table, TableBody, TableCell, TableHead, TableRow} from '@mui/material'
import HintButton from '../shared/pageBaseComponents/HintButton'
import {getLinkIconComponent} from '../assocDirector/AssocDirectorCommon'
import {
    formatDateTimeForUpdateDateFromDate,
    formatDatetimeWithDayOfTheWeekFromDate,
    formatDateWithDayOfTheWeekFromDate
} from '../../formatters/formatDate'
import {AssocIdName} from './model/AssocIdName'
import {Colors, FontSizeInPixel, FontWeights} from '../../styledComponents/Constants'
import {NoWrapSpan} from '../assocDirector/shared/AssocComponents'
import {AdminGeneralMeetingFile} from './model/GeneralMeetingFile'
import {StyledDiv} from '../../styledComponents/StyledTypeDefinitions'
import {Messages} from '../../Messages'
import {ProposalByAssoc} from './model/ProposalByAssoc'
import {AdminGeneralMeetingDetailInfo} from './model/GeneralMeeting'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import {GetApp, InfoOutlined} from '@mui/icons-material'
import CancelIcon from '@mui/icons-material/Lock'
import LanguageIcon from '@mui/icons-material/Language'
import HowToVoteIcon from '@mui/icons-material/HowToVote'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import ChatIcon from '@mui/icons-material/Chat'

import {
    AVAILABLE_OR_NOT,
    GENERAL_MEETING_STATUS,
    ROLE_FOR_ADMINISTRATOR,
    VOTING_PREFERENCE_RESPONSE
} from '../../CodeMaster'
import moment from 'moment/moment'
import NatCurrentDateTimeRepo
    from '../../__NoAutomatedTests/components/currentDateTime/repository/NatCurrentDateTimeRepo'
import {KEY_LOGIN_USER_ROLE_ID, SessionStorageHelper} from '../../SessionStorageHelper'
import {DeleteAlertIcon} from '../../styledComponents/adminPage/IconCreator'
import {VotingPreferenceSummary, VotingPreferenceSummaryByAssoc} from './model/VotingPreferenceSummaryByAssoc'
import {GeneralMeetingProposal} from './model/GeneralMeetingProposal'
import {QuestionSummaryByAssoc} from './model/QuestionSummaryByAssoc'
import {Location} from 'react-router-dom'
import {formatDatetimeWithDayOfTheWeek} from '../../formatters/formatMoment'
import {formatNumberWithCommas} from '../../formatters/formatNumberWithCommas'
import ColonSeparatedItem from '../shared/ColonSeparatedItem'

const NOT_REGISTERED: string = '登録されていません'

interface Props {
    location: Location
    condominiumCd: string
    generalMeetingId: number
    adminGeneralMeetingRepo: AdminGeneralMeetingRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    eventEmitter: EventEmitterExAdmin
}

interface LocationState {
    initialMessage: HeadingAreaMessageParams
}

interface State {
    generalMeetingInfo: Nullable<AdminGeneralMeetingDetailInfo>
    votingPreferenceSummaryList: Nullable<VotingPreferenceSummaryByAssoc[]>
    questionSummaryList: Nullable<QuestionSummaryByAssoc[]>
    currentDateTime: Nullable<Date>
    hasEditAuthority: boolean
    loginUserRoleId: Nullable<string>
    downloadButtonDisable: boolean
}

class AdminGeneralMeetingDetailPage extends Component<Props & AdminPagePropsSet, State> {
    constructor(props: Readonly<Props & AdminPagePropsSet>) {
        super(props)

        this.state = {
            generalMeetingInfo: null,
            votingPreferenceSummaryList: null,
            questionSummaryList: null,
            currentDateTime: null,
            hasEditAuthority: false,
            loginUserRoleId: SessionStorageHelper.getStringItemOrNull(KEY_LOGIN_USER_ROLE_ID),
            downloadButtonDisable: false
        }
    }

    async componentDidMount(): Promise<void> {
        const {eventEmitter} = this.props
        await eventEmitter.showLoading()
        try {
            await this.fetchData()
        } finally {
            eventEmitter.closeLoading()
        }
        await this.showInitialMessage()
    }

    async fetchData(): Promise<void> {
        const {
            condominiumCd,
            generalMeetingId,
            adminGeneralMeetingRepo,
            currentDateTimeRepo,
            eventEmitter,
            asyncCallFunction,
            navigate,
        } = this.props

        const generalMeetingInfoResponse: Nullable<GetGeneralMeetingInfoResponse> =
            await asyncCallFunction(
                adminGeneralMeetingRepo.getGeneralMeetingInfo.bind(
                    adminGeneralMeetingRepo,
                    condominiumCd,
                    generalMeetingId
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(eventEmitter)
                },
            )
        if (generalMeetingInfoResponse === null) {
            navigate('/admin/error')
            return
        }

        const votingPreferenceSummaryResponse: Nullable<GetVotingPreferenceSummaryResponse> =
            await asyncCallFunction(
                adminGeneralMeetingRepo.getVotingPreferenceSummary.bind(
                    adminGeneralMeetingRepo,
                    condominiumCd,
                    generalMeetingId
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(eventEmitter)
                },
            )
        if (votingPreferenceSummaryResponse === null) {
            navigate('/admin/error')
            return
        }

        let questionSummaryList: QuestionSummaryByAssoc[] = []
        if (generalMeetingInfoResponse.details.questionAvailability === AVAILABLE_OR_NOT.AVAILABLE) {
            const questionSummaryResponse: Nullable<GetQuestionSummaryResponse>
                = await asyncCallFunction(
                adminGeneralMeetingRepo.getQuestionSummary.bind(
                    adminGeneralMeetingRepo,
                    condominiumCd,
                    generalMeetingId
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(eventEmitter)
                },
            )
            if (questionSummaryResponse === null) {
                navigate('/admin/error')
                return
            }
            questionSummaryList = questionSummaryResponse.details
        }

        const currentDateTimeResponse = await asyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )
        if (currentDateTimeResponse === null) {
            navigate('/admin/error')
            return
        }
        const currentDateTime = currentDateTimeResponse.details
            ? new Date(currentDateTimeResponse.details.currentDateTime)
            : null

        const hasEditAuthority = judgeAuthorityCheck('EDIT-GENERAL_MEETING')

        this.setState({
            generalMeetingInfo: generalMeetingInfoResponse.details,
            votingPreferenceSummaryList: votingPreferenceSummaryResponse.details,
            questionSummaryList: questionSummaryList,
            currentDateTime: currentDateTime,
            hasEditAuthority: hasEditAuthority,
        })
    }

    async showInitialMessage(): Promise<void> {
        const {eventEmitter, navigate, location} = this.props

        if (!location || !location.state) return

        const locationState: LocationState = location.state
        if (!locationState.initialMessage) return

        navigate(location.pathname, {replace: true, state: {},})
        await eventEmitter.showHeadingAreaMessage(locationState.initialMessage)
    }

    handleEditGeneralMeetingButton = (): void => {
        this.props.navigate(`/admin/condominiums/${this.props.condominiumCd}/general-meetings/${this.props.generalMeetingId}/edit`)
    }

    handleCancelLink = async (): Promise<void> => {
        const {generalMeetingInfo} = this.state
        if (!generalMeetingInfo) return
        if (!generalMeetingInfo.generalMeetingName) return

        await this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: Messages.WGMTA0005.errorLevel,
                message: Messages.WGMTA0005.toString(generalMeetingInfo.generalMeetingName),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: this.handleCancelOkClick,
                    },
                ],
            },
        )
    }

    handleCancelOkClick = async (): Promise<void> => {
        const {
            eventEmitter,
            asyncCallFunction,
            adminGeneralMeetingRepo,
            condominiumCd,
        } = this.props

        const {generalMeetingInfo} = this.state
        if (!generalMeetingInfo) return

        eventEmitter.closeDialog()

        await eventEmitter.showLoading()
        try {
            const result = await asyncCallFunction(
                adminGeneralMeetingRepo.updateGeneralMeetingStatus.bind(
                    adminGeneralMeetingRepo,
                    condominiumCd,
                    generalMeetingInfo.generalMeetingId,
                    {
                        generalMeetingStatus: GENERAL_MEETING_STATUS.CANCEL.value,
                        version: generalMeetingInfo.version
                    }
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(eventEmitter)
                    await this.fetchData()
                },
            )
            if (!result) return

            await this.fetchData()

            await this.props.eventEmitter.showHeadingAreaMessage({
                title: result.information.title || '',
                messageLevel: result.information.type,
                detail: result.information.message,
            })
        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleCancelClick = (): void => {
        this.props.eventEmitter.closeDialog()
    }

    handleDeleteLink = async (): Promise<void> => {
        const {generalMeetingInfo} = this.state
        if (!generalMeetingInfo) return
        const generalMeetingName = generalMeetingInfo.generalMeetingName ?? '総会の開催案内'

        await this.props.eventEmitter.showDialog({
                title: '削除確認',
                titleIcon: <DeleteAlertIcon/>,
                errorLevel: Messages.WGMTA0006.errorLevel,
                message: Messages.WGMTA0006.toString(generalMeetingName),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: '削除する',
                        buttonHandler: this.handleDeleteOkClick,
                        color: Colors.Alert
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleCancelClick,
                    },
                ],
            },
        )
    }

    handleDeleteOkClick = async (): Promise<void> => {
        const {
            eventEmitter,
            asyncCallFunction,
            adminGeneralMeetingRepo,
            condominiumCd,
            navigate
        } = this.props

        const {generalMeetingInfo} = this.state
        if (!generalMeetingInfo) return

        eventEmitter.closeDialog()

        await eventEmitter.showLoading()
        try {
            const result = await asyncCallFunction(
                adminGeneralMeetingRepo.deleteGeneralMeeting.bind(
                    adminGeneralMeetingRepo,
                    condominiumCd,
                    generalMeetingInfo.generalMeetingId,
                    generalMeetingInfo.version
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(eventEmitter)
                    await this.fetchData()
                },
            )
            if (!result) return

            const initialMessage = {
                title: result.information.title || '',
                messageLevel: result.information.type,
                detail: result.information.message
            }

            navigate(
                `/admin/condominiums/${this.props.condominiumCd}/general-meetings`,
                {
                    state: {
                        initialMessage,
                    },
                }
            )

        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleVotingPreferenceButton = async (isSetManualClosedDatetime: boolean): Promise<void> => {
        const {generalMeetingInfo} = this.state
        if (!generalMeetingInfo) return
        if (!generalMeetingInfo.generalMeetingName) return

        const confirmationMessage = isSetManualClosedDatetime
            ? Messages.WGMTA0003
            : Messages.WGMTA0004

        await this.props.eventEmitter.showDialog({
                title: '確認',
                errorLevel: confirmationMessage.errorLevel,
                message: confirmationMessage.toString(generalMeetingInfo.generalMeetingName),
                buttonProps: [
                    {
                        isDefault: false,
                        buttonLabel: 'キャンセル',
                        buttonHandler: this.handleCancelClick,
                    },
                    {
                        isDefault: false,
                        buttonLabel: 'OK',
                        buttonHandler: () => this.handleVotingPreferenceOkClick(isSetManualClosedDatetime),
                    },
                ],
            },
        )
    }

    handleVotingPreferenceOkClick = async (isSetManualClosedDatetime: boolean): Promise<void> => {
        const {
            eventEmitter,
            asyncCallFunction,
            adminGeneralMeetingRepo,
            condominiumCd,
        } = this.props

        const {generalMeetingInfo} = this.state
        if (!generalMeetingInfo) return

        eventEmitter.closeDialog()

        await eventEmitter.showLoading()
        try {
            const result = await asyncCallFunction(
                adminGeneralMeetingRepo.updateVotingPreferenceManualDeadline.bind(
                    adminGeneralMeetingRepo,
                    condominiumCd,
                    generalMeetingInfo.generalMeetingId,
                    {
                        isSetManualClosedDatetime: isSetManualClosedDatetime,
                        version: generalMeetingInfo.version
                    }
                ),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(eventEmitter)
                    await this.fetchData()
                },
            )
            if (!result) return

            await this.fetchData()

            await this.props.eventEmitter.showHeadingAreaMessage({
                title: result.information.title || '',
                messageLevel: result.information.type,
                detail: result.information.message,
            })
        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleDownloadVotingPreferenceResultButton = async (): Promise<void> => {
        this.setState({downloadButtonDisable: true}, async () => {
            await this.props.eventEmitter.showLoading()
            try {
                const tempLink: HTMLAnchorElement = document.createElement('a')
                tempLink.href = this.props.adminGeneralMeetingRepo
                    .getVotingPreferenceResultExcel(this.props.condominiumCd, this.props.generalMeetingId)
                document.body.appendChild(tempLink)
                tempLink.click()
            } finally {
                this.props.eventEmitter.closeLoading()
            }
            this.setState({downloadButtonDisable: false})
        })
    }

    handleDownloadQuestionButton = async (): Promise<void> => {
        this.setState({downloadButtonDisable: true}, async () => {
            await this.props.eventEmitter.showLoading()
            try {
                const tempLink: HTMLAnchorElement = document.createElement('a')
                tempLink.href = this.props.adminGeneralMeetingRepo
                    .getQuestionCsv(this.props.condominiumCd, this.props.generalMeetingId)
                document.body.appendChild(tempLink)
                tempLink.click()
            } finally {
                this.props.eventEmitter.closeLoading()
            }
            this.setState({downloadButtonDisable: false})
        })
    }

    render(): ReactNode {
        const {
            generalMeetingInfo,
            votingPreferenceSummaryList,
            questionSummaryList,
            currentDateTime,
            hasEditAuthority,
            loginUserRoleId,
        } = this.state
        if (!generalMeetingInfo || !votingPreferenceSummaryList || !questionSummaryList || !currentDateTime) return null

        const generalMeetingStatus = generalMeetingInfo.generalMeetingStatus

        const canDeleteGeneralMeeting = loginUserRoleId === ROLE_FOR_ADMINISTRATOR.SYSTEM_ADMIN.value
            || generalMeetingStatus === GENERAL_MEETING_STATUS.DRAFT
            || generalMeetingStatus === GENERAL_MEETING_STATUS.BEFORE_RELEASE

        const displayGeneralMeetingName = generalMeetingInfo.generalMeetingName ?? NOT_REGISTERED

        const displayGeneralMeetingDate = generalMeetingInfo.generalMeetingDate
            ? formatDateWithDayOfTheWeekFromDate(generalMeetingInfo.generalMeetingDate)
            : NOT_REGISTERED

        const displayStartTime = generalMeetingInfo.startTime !== null
            ? generalMeetingInfo.startTime.substring(0, 5) + '〜'
            : NOT_REGISTERED

        return <>
            <Box height={'16px'}/>
            {hasEditAuthority &&
                <TopArea>
                    <div>
                        {generalMeetingStatus !== GENERAL_MEETING_STATUS.CANCEL
                            && <AdminDefaultButton onClick={this.handleEditGeneralMeetingButton}>
                                編集する
                            </AdminDefaultButton>
                        }
                    </div>
                    <RowFlexContainer>
                        {generalMeetingStatus === GENERAL_MEETING_STATUS.AFTER_RELEASE
                            && <StyledCancelLink onClick={this.handleCancelLink}>
                                公開を中止する
                            </StyledCancelLink>
                        }
                        <StyledDeleteLink
                            onClick={this.handleDeleteLink}
                            disabled={!canDeleteGeneralMeeting}
                        >
                            削除する
                        </StyledDeleteLink>
                        <HintButton message={Messages.HGMTA0004.message}/>
                    </RowFlexContainer>
                </TopArea>
            }

            <MainArea>
                <LeftMainArea>
                    {this.renderReleaseSettingPaper(generalMeetingInfo)}
                    {this.renderVotingPreferenceSettingPaper(generalMeetingInfo, currentDateTime, hasEditAuthority)}
                    {this.renderQuestionSettingPaper(generalMeetingInfo, currentDateTime)}
                    {this.renderVotingPreferenceSummaryPaper(generalMeetingStatus, votingPreferenceSummaryList)}
                    {this.renderQuestionSummaryPaper(
                        generalMeetingInfo.questionAvailability,
                        generalMeetingStatus,
                        questionSummaryList
                    )}
                </LeftMainArea>

                <RightMainArea>
                    <ComponentPaper>
                        <PaperTitleArea>
                            <PaperTitleText>開催要項</PaperTitleText>
                        </PaperTitleArea>
                        <Divider/>
                        <PaperMainArea>
                            <ColonSeparatedItem label={'総会名'}>
                                {displayGeneralMeetingName}
                            </ColonSeparatedItem>
                            <Box>
                                {this.renderAssocNames(generalMeetingInfo.assocIdNameList)}
                            </Box>
                            <Box>
                                開催日：{displayGeneralMeetingDate}
                            </Box>
                            <Box>
                                開始時間：{displayStartTime}
                            </Box>
                        </PaperMainArea>
                    </ComponentPaper>
                    <ComponentPaper>
                        <PaperTitleArea>
                            <PaperTitleText>連絡内容</PaperTitleText>
                        </PaperTitleArea>
                        <Divider/>
                        <NoticeContentArea>
                            {generalMeetingInfo.noticeContent ?? NOT_REGISTERED}
                        </NoticeContentArea>
                    </ComponentPaper>
                    <ComponentPaper>
                        <PaperTitleArea>
                            <PaperTitleText>資料</PaperTitleText>
                        </PaperTitleArea>
                        <Divider/>
                        <PaperMainArea>
                            {
                                (generalMeetingInfo.fileList.length > 0) ?
                                    <StyledTable>
                                        <TableHead>
                                            <TableRow>
                                                <FileNameCell>
                                                    資料名
                                                </FileNameCell>
                                                <FileRegisteredUserCell>
                                                    登録者
                                                </FileRegisteredUserCell>
                                                <FileRegisteredDatetimeCell>
                                                    登録日時
                                                </FileRegisteredDatetimeCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {this.renderFileInfoTable(generalMeetingInfo.fileList)}
                                        </TableBody>
                                    </StyledTable>
                                    : NOT_REGISTERED
                            }
                        </PaperMainArea>
                    </ComponentPaper>
                    <ComponentPaper>
                        <PaperTitleArea>
                            <PaperTitleText>議案</PaperTitleText>
                        </PaperTitleArea>
                        <Divider/>
                        <PaperMainArea>
                            {generalMeetingInfo.proposalList.length > 0
                                ? this.renderProposalInfos(
                                    generalMeetingInfo.proposalList,
                                    generalMeetingInfo.assocIdNameList
                                )
                                : NOT_REGISTERED
                            }
                        </PaperMainArea>
                    </ComponentPaper>
                </RightMainArea>
            </MainArea>

            <FooterArea>
                <BackLink to={`/admin/condominiums/${this.props.condominiumCd}/general-meetings`}>
                    総会一覧へ戻る
                </BackLink>
            </FooterArea>
        </>
    }

    private renderReleaseSettingPaper = (generalMeetingInfo: AdminGeneralMeetingDetailInfo): ReactNode => {
        return <ComponentPaper>
            <PaperTitleArea>
                <PaperTitleText>公開設定</PaperTitleText>
                <StatusLabel>
                    {generalMeetingInfo.generalMeetingStatus === GENERAL_MEETING_STATUS.AFTER_RELEASE
                        ? <StyledLanguageIcon/>
                        : <StyledCancelIcon/>
                    }
                    {generalMeetingInfo.generalMeetingStatus.label}
                </StatusLabel>
            </PaperTitleArea>
            <Divider/>
            <PaperMainArea>
                {this.renderReleaseSettingStatusMessage()}
                公開開始日：{
                generalMeetingInfo.releaseDate
                    ? formatDateWithDayOfTheWeekFromDate(generalMeetingInfo.releaseDate)
                    : NOT_REGISTERED
            }
                {(generalMeetingInfo.generalMeetingStatus === GENERAL_MEETING_STATUS.CANCEL
                        && generalMeetingInfo.cancelDatetime)
                    && <CancelDateTimeText>
                        <div>【公開中止】</div>
                        <div>
                            {formatDatetimeWithDayOfTheWeekFromDate(generalMeetingInfo.cancelDatetime)}
                            に公開中止にされました
                        </div>
                    </CancelDateTimeText>
                }
            </PaperMainArea>
        </ComponentPaper>
    }

    private renderReleaseSettingStatusMessage = (): ReactNode => {
        const generalMeetingInfo = this.state.generalMeetingInfo
        if (!generalMeetingInfo) return null

        switch (generalMeetingInfo.generalMeetingStatus) {
            case GENERAL_MEETING_STATUS.DRAFT:
                return (
                    <StatusMessageText isDraft={true}>
                        <StyledReportProblemOutlinedIcon/>
                        公開の準備を行ってください
                    </StatusMessageText>
                )
            case GENERAL_MEETING_STATUS.BEFORE_RELEASE:
                if (!generalMeetingInfo.releaseDate) return null

                const displayMessage
                    = moment(generalMeetingInfo.releaseDate.toISOString()).format('YYYY年M月D日') + ' 0時に公開します'

                return (
                    <StatusMessageText isDraft={false}>
                        <StyledInfoOutlinedIcon/>
                        {displayMessage}
                    </StatusMessageText>
                )
            case GENERAL_MEETING_STATUS.AFTER_RELEASE:
            case GENERAL_MEETING_STATUS.CANCEL:
            default:
                return null
        }
    }

    private renderVotingPreferenceSettingPaper = (
        generalMeetingInfo: AdminGeneralMeetingDetailInfo,
        currentDateTime: Date,
        editMode: boolean,
    ): ReactNode => {
        const isNotCanceled = generalMeetingInfo.generalMeetingStatus !== GENERAL_MEETING_STATUS.CANCEL

        const hasVotingPreferenceDeadline = generalMeetingInfo.votingPreferenceDeadline !== null
        const isVotingPreferenceAcceptanceActive = hasVotingPreferenceDeadline
            && generalMeetingInfo.generalMeetingStatus === GENERAL_MEETING_STATUS.AFTER_RELEASE
            && generalMeetingInfo.generalMeetingDate !== null
            && generalMeetingInfo.startTime !== null

        const dateStr = generalMeetingInfo.generalMeetingDate
            ? moment(generalMeetingInfo.generalMeetingDate).format('YYYY-MM-DD')
            : null
        const generalMeetingStartDatetime = dateStr && generalMeetingInfo.startTime
            ? moment(`${dateStr} ${generalMeetingInfo.startTime}`, 'YYYY-MM-DD HH:mm')
            : null

        const isNotVotingPreferenceClosedManually = isVotingPreferenceAcceptanceActive
            && generalMeetingInfo.votingPreferenceManualClosedDatetime === null
        const isBeforeGeneralMeetingStartDatetime = isVotingPreferenceAcceptanceActive
            && generalMeetingStartDatetime !== null
            && moment(currentDateTime).isBefore(generalMeetingStartDatetime)

        const votingPreferenceDeadlineText: string = hasVotingPreferenceDeadline
            ? '案内上の期限：' + formatDateWithDayOfTheWeekFromDate(generalMeetingInfo.votingPreferenceDeadline!)
            : NOT_REGISTERED

        let closedInfoText: Nullable<string> = null
        if (isNotVotingPreferenceClosedManually && !isBeforeGeneralMeetingStartDatetime) {
            closedInfoText = formatDatetimeWithDayOfTheWeek(generalMeetingStartDatetime!)
                + 'に自動で締め切りました'
        } else if (generalMeetingInfo.votingPreferenceManualClosedDatetime !== null) {
            closedInfoText =
                formatDatetimeWithDayOfTheWeekFromDate(generalMeetingInfo.votingPreferenceManualClosedDatetime)
                + 'に手動で締め切りました'
        }

        return <ComponentPaper>
            <PaperTitleArea>
                <PaperTitleText>意思表明の受付設定</PaperTitleText>
                {isVotingPreferenceAcceptanceActive
                    && <StatusLabel>
                        {isNotVotingPreferenceClosedManually && isBeforeGeneralMeetingStartDatetime
                            ? <>
                                <StyledHowToVoteIcon/>受付中
                            </>
                            : <>
                                <StyledTaskAltIcon/>締切済
                            </>
                        }
                    </StatusLabel>
                }
            </PaperTitleArea>
            <Divider/>
            <PaperMainArea>
                {votingPreferenceDeadlineText}
                {(isNotCanceled && hasVotingPreferenceDeadline && editMode)
                    && <Box height={'16px'}/>
                }
                {isBeforeGeneralMeetingStartDatetime && editMode
                    && (isNotVotingPreferenceClosedManually
                            ? <VotingPreferenceCloseButton onClick={() => this.handleVotingPreferenceButton(true)}>
                                意思表明を締め切る
                            </VotingPreferenceCloseButton>
                            : <VotingPreferenceReopenButton onClick={() => this.handleVotingPreferenceButton(false)}>
                                意思表明の受付を再開する
                            </VotingPreferenceReopenButton>
                    )
                }
                {(isNotCanceled && hasVotingPreferenceDeadline)
                    && (closedInfoText !== null
                            ? <>
                                {!editMode && <Box height={'16px'}/>}
                                <div>【締め切り日時】</div>
                                <div>{closedInfoText}</div>
                            </>
                            : (editMode && (<CautionText>
                                    <div>※</div>
                                    <div>手動で締め切らない場合、設定した総会開始日時に自動で締め切られます</div>
                                </CautionText>
                            ))
                    )
                }
            </PaperMainArea>
        </ComponentPaper>
    }

    private renderQuestionSettingPaper = (
        generalMeetingInfo: AdminGeneralMeetingDetailInfo,
        currentDateTime: Date,
    ): ReactNode => {
        const isQuestionAvailable = generalMeetingInfo.questionAvailability === AVAILABLE_OR_NOT.AVAILABLE
        const hasQuestionDeadline = isQuestionAvailable && generalMeetingInfo.questionDeadline !== null
        const isQuestionAcceptanceActive = hasQuestionDeadline
            && generalMeetingInfo.generalMeetingStatus === GENERAL_MEETING_STATUS.AFTER_RELEASE
        const isQuestionOpen = isQuestionAcceptanceActive
            && moment(currentDateTime).isSameOrBefore(moment(generalMeetingInfo.questionDeadline).endOf('day'))

        const questionAcceptanceStatusText: string = isQuestionAvailable
            ? '質問を受け付ける'
            : '質問を受け付けない'
        const questionDeadlineText: string = hasQuestionDeadline
            ? formatDateWithDayOfTheWeekFromDate(generalMeetingInfo.questionDeadline!)
            : NOT_REGISTERED

        return <ComponentPaper>
            <PaperTitleArea>
                <PaperTitleText>質問の受付設定</PaperTitleText>
                {isQuestionAcceptanceActive && <StatusLabel>
                    {isQuestionOpen
                        ? <>
                            <StyledChatBubbleIcon/>受付中
                        </>
                        : <>
                            <StyledTaskAltIcon/>締切済
                        </>
                    }
                </StatusLabel>
                }
            </PaperTitleArea>
            <Divider/>
            <PaperMainArea>
                受付可否：{questionAcceptanceStatusText}
                {isQuestionAvailable && <div>受付期限：{questionDeadlineText}</div>}
            </PaperMainArea>
        </ComponentPaper>
    }

    private renderVotingPreferenceSummaryPaper = (
        generalMeetingStatus: GENERAL_MEETING_STATUS,
        votingPreferenceSummaryList: VotingPreferenceSummaryByAssoc[],
    ): ReactNode => {
        const generalMeetingReleased =
            generalMeetingStatus === GENERAL_MEETING_STATUS.AFTER_RELEASE
            || generalMeetingStatus === GENERAL_MEETING_STATUS.CANCEL

        const hasOutputExcelAuthority = judgeAuthorityCheck('OUTPUT_EXCEL-GENERAL_MEETING')

        const isSingleAssoc = votingPreferenceSummaryList.length <= 1

        const emptySummary: VotingPreferenceSummary = {
            attendanceCount: 0,
            proxyCount: 0,
            votingCount: 0,
            noResponseCount: 0,
        }

        return <ComponentPaper>
            <PaperBlueTitleArea>
                <PaperTitleText>意思表明の受付状況</PaperTitleText>
                {(generalMeetingReleased && hasOutputExcelAuthority)
                    && <DownloadButton
                        onClick={this.handleDownloadVotingPreferenceResultButton}
                        disabled={this.state.downloadButtonDisable}
                    >
                        <StyledGetApp/>
                        ダウンロード
                    </DownloadButton>
                }
            </PaperBlueTitleArea>
            <Divider/>
            <PaperMainArea>
                {isSingleAssoc
                    ? this.renderVotingPreferenceCounts(
                        generalMeetingReleased,
                        votingPreferenceSummaryList[0]?.votingPreferenceSummary ?? emptySummary
                    )
                    : votingPreferenceSummaryList.map((votingPreferenceSummaryByAssoc, index) => (
                        <FullWidthColumnFlexContainer key={index}>
                            <AssocNameLabel>
                                {votingPreferenceSummaryByAssoc.assocIdName.assocName}
                            </AssocNameLabel>
                            {this.renderVotingPreferenceCounts(
                                generalMeetingReleased,
                                votingPreferenceSummaryByAssoc.votingPreferenceSummary
                            )}
                            <Box height={'16px'}/>
                            {index !== votingPreferenceSummaryList.length - 1
                                && <>
                                    <StyledDivider/>
                                    <Box height={'24px'}/>
                                </>
                            }
                        </FullWidthColumnFlexContainer>
                    ))
                }
                <AdditionalNote>
                    ※素敵ネットからの議決権行使された議決権数
                </AdditionalNote>
            </PaperMainArea>
        </ComponentPaper>
    }

    private renderVotingPreferenceCounts = (
        generalMeetingReleased: boolean,
        votingPreferenceSummary: VotingPreferenceSummary,
    ): ReactNode => {
        const countOrDash = (count: number) => generalMeetingReleased ? formatNumberWithCommas(count) : 'ー'
        return <CountResultArea>
            <FullWidthRowFlexContainer>
                {VOTING_PREFERENCE_RESPONSE.ATTENDANCE.label}
                <CountText>
                    <CountLabel>{countOrDash(votingPreferenceSummary.attendanceCount)}</CountLabel>
                    個
                </CountText>
            </FullWidthRowFlexContainer>
            <FullWidthRowFlexContainer>
                {VOTING_PREFERENCE_RESPONSE.PROXY.label}
                <CountText>
                    <CountLabel>{countOrDash(votingPreferenceSummary.proxyCount)}</CountLabel>
                    個
                </CountText>
            </FullWidthRowFlexContainer>
            <FullWidthRowFlexContainer>
                {VOTING_PREFERENCE_RESPONSE.VOTING.label}
                <CountText>
                    <CountLabel>{countOrDash(votingPreferenceSummary.votingCount)}</CountLabel>
                    個
                </CountText>
            </FullWidthRowFlexContainer>
            <FullWidthRowFlexContainer>
                未回答
                <CountText>
                    <CountLabel>{countOrDash(votingPreferenceSummary.noResponseCount)}</CountLabel>
                    個
                </CountText>
            </FullWidthRowFlexContainer>
        </CountResultArea>
    }

    private renderQuestionSummaryPaper = (
        questionAvailability: AVAILABLE_OR_NOT,
        generalMeetingStatus: GENERAL_MEETING_STATUS,
        questionSummaryList: QuestionSummaryByAssoc[],
    ): ReactNode => {
        if (questionAvailability === AVAILABLE_OR_NOT.UNAVAILABLE) return

        const generalMeetingReleased =
            generalMeetingStatus === GENERAL_MEETING_STATUS.AFTER_RELEASE
            || generalMeetingStatus === GENERAL_MEETING_STATUS.CANCEL

        const hasOutputAuthority = judgeAuthorityCheck('OUTPUT-GENERAL_MEETING')

        const isSingleAssoc = questionSummaryList.length <= 1

        return <ComponentPaper>
            <PaperBlueTitleArea>
                <PaperTitleText>質問の受付状況</PaperTitleText>
                {(generalMeetingReleased && hasOutputAuthority)
                    && <DownloadButton
                        disabled={this.state.downloadButtonDisable}
                        onClick={this.handleDownloadQuestionButton}
                    >
                        <StyledGetApp/>
                        ダウンロード
                    </DownloadButton>
                }
            </PaperBlueTitleArea>
            <Divider/>
            <PaperMainArea>
                {isSingleAssoc
                    ? this.renderQuestionCounts(generalMeetingReleased, questionSummaryList[0]?.questionCount ?? 0)
                    : questionSummaryList.map((questionSummaryByAssoc, index) => (
                        <FullWidthColumnFlexContainer key={index}>
                            <AssocNameLabel>
                                {questionSummaryByAssoc.assocIdName.assocName}
                            </AssocNameLabel>
                            {this.renderQuestionCounts(
                                generalMeetingReleased,
                                questionSummaryByAssoc.questionCount
                            )}
                            <Box height={'16px'}/>
                            {index !== questionSummaryList.length - 1
                                && <>
                                    <StyledDivider/>
                                    <Box height={'24px'}/>
                                </>
                            }
                        </FullWidthColumnFlexContainer>
                    ))
                }
                <AdditionalNote>
                    ※素敵ネットからの質問件数
                </AdditionalNote>
            </PaperMainArea>
        </ComponentPaper>
    }

    private renderQuestionCounts = (
        generalMeetingReleased: boolean,
        questionCount: number,
    ): ReactNode => {
        const countOrDash = (count: number) => generalMeetingReleased ? formatNumberWithCommas(count) : 'ー'
        return <CountResultArea>
            <FullWidthRowFlexContainer>
                質問受付数
                <CountText>
                    合計
                    <CountLabel>{countOrDash(questionCount)}</CountLabel>
                    件
                </CountText>
            </FullWidthRowFlexContainer>
        </CountResultArea>
    }

    private renderAssocNames = (list: AssocIdName[]) => {
        if (list.length === 0) {
            return <>管理組合：{NOT_REGISTERED}</>
        }

        if (list.length === 1) {
            return (
                <ColonSeparatedItem label={'管理組合'}>
                    {list[0].assocName}
                </ColonSeparatedItem>
            )
        }

        return (
            <div>
                <div>管理組合：</div>
                {list.map(assoc => (
                    <ItemRow key={assoc.assocId}>
                        <ItemLabel>・</ItemLabel>
                        <ItemValue>{assoc.assocName}</ItemValue>
                    </ItemRow>
                ))}
            </div>
        )
    }

    private renderFileInfoTable(fileList: AdminGeneralMeetingFile[]): ReactNode {
        return fileList.map((file) =>
            <TableRow key={file.fileNumber}>
                <TableCell>
                    <StyledLink to={file.fileDownloadPath} target={'_blank'}>
                        <LeftRowFlexContainer>
                            <FileIconStyle>
                                {getLinkIconComponent(getFileExtension(file.fileName))}
                            </FileIconStyle>
                            <FileNameStyle>
                                {file.fileName}
                            </FileNameStyle>
                        </LeftRowFlexContainer>
                    </StyledLink>
                </TableCell>
                <TableCell>
                    <NoWrapSpan>
                        <FileRegisteredUserText>
                            {
                                file.registeredUserFamilyName + ' ' + file.registeredUserFirstName
                                + '　' + file.registeredUserRoleId.label
                            }
                        </FileRegisteredUserText>
                    </NoWrapSpan>
                </TableCell>
                <NowrapCell>
                    {formatDateTimeForUpdateDateFromDate(file.registeredDatetime)}
                </NowrapCell>
            </TableRow>
        )
    }

    private renderProposalInfos(proposalList: ProposalByAssoc[], assocIdNameList: AssocIdName[]): ReactNode {
        if (proposalList.length === 1) {
            return this.renderProposalInfoTable(proposalList[0].generalMeetingProposalList)
        }

        return (
            <>
                {proposalList.map((proposalAssoc, index) => (
                    <FullWidthLeftColumnFlexContainer key={proposalAssoc.assocId}>
                        <AssocNameText>
                            {
                                assocIdNameList.find(
                                    assoc => assoc.assocId === proposalAssoc.assocId,
                                )?.assocName
                            }
                        </AssocNameText>
                        <Box height={'8px'}/>
                        {this.renderProposalInfoTable(proposalAssoc.generalMeetingProposalList)}
                        {index < proposalList.length - 1 && (
                            <>
                                <Box height={'16px'}/>
                                <Divider style={{width: '100%'}}/>
                                <Box height={'24px'}/>
                            </>
                        )}
                    </FullWidthLeftColumnFlexContainer>
                ))}
            </>
        )
    }

    private renderProposalInfoTable(proposalDetailInfoList: GeneralMeetingProposal[]): ReactNode {
        const NONE = 'ー'

        return (
            <StyledTable>
                <TableHead>
                    <TableRow>
                        <ProposalNumberCell>議案番号</ProposalNumberCell>
                        <ProposalResolutionTypeCell>決議の種類</ProposalResolutionTypeCell>
                        <ProposalHeaderCell>議案名</ProposalHeaderCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {proposalDetailInfoList.map((proposalDetailInfo) => (
                        <TableRow key={proposalDetailInfo.proposalId}>
                            <TableCell style={{verticalAlign: 'top'}}>
                                <NoWrapSpan>
                                    第{proposalDetailInfo.proposalNumber}号議案
                                </NoWrapSpan>
                            </TableCell>

                            <TableCell style={{verticalAlign: 'top'}}>
                                <NoWrapSpan>
                                    {proposalDetailInfo.resolutionType
                                        ? proposalDetailInfo.resolutionType.label
                                        : NONE}
                                </NoWrapSpan>
                            </TableCell>
                            <TableCell style={{overflowWrap: 'anywhere'}}>
                                {proposalDetailInfo.proposalName ?? NONE}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </StyledTable>
        )
    }
}

export default createAdminPage(AdminGeneralMeetingDetailPage)

const TopArea = styled(FullWidthRowFlexContainer)`
  justify-content: space-between;
  margin-bottom: 8px;
`

const StyledCancelLink = styled(CancelLink)`
  color: ${Colors.Alert};
  margin-right: 20px;
`

const StyledDeleteLink = styled(DeleteLink)`
  color: ${Colors.Alert};
  margin-right: 4px;
`

const MainArea = styled(LeftTopRowFlexContainer)`
  gap: 16px;
`

const LeftMainArea = styled(ColumnFlexContainer)`
  width: 345px;
  flex-shrink: 0;
  gap: 16px;
`

const RightMainArea = styled(ColumnFlexContainer)`
  flex-grow: 1;
  gap: 16px;
`

const ComponentPaper = styled(Paper).attrs({
    elevation: 2,
})`
  width: 100%;
`

const PaperTitleArea = styled(LeftRowFlexContainer)`
  padding: 0 16px;
  height: 56px;
`

const PaperBlueTitleArea = styled(PaperTitleArea)`
  background-color: #E2E5F4;
  justify-content: space-between;
`

const PaperTitleText = styled.div`
  font-size: ${FontSizeInPixel.Body}px;
  font-weight: ${FontWeights.Bold};
`

const DownloadButton = styled(AdminPrimaryOutlinedButton)`
  background-color: ${Colors.Surface};
`

const StyledGetApp = styled(GetApp)`
  font-size: 20px;
  margin-right: 15px;
`

const AssocNameText = styled.div`
  font-size: ${FontSizeInPixel.Body}px;
  word-break: break-word;
  overflow-wrap: anywhere;
`

const StyledTable = styled(Table)`
  &.MuiTable-root {
    border: 1px solid rgba(224, 224, 224, 1);
  }
`

const FileHeaderCell = styled(TableCell)`
  &.MuiTableCell-root {
    padding-top: 4px;
    padding-bottom: 4px;
    font-size: 0.85em;
    color: ${Colors.AccentDark};
  }

  background-color: ${Colors.Background};
`

const FileNameCell = styled(FileHeaderCell)`
  width: 50%;
`

const FileRegisteredUserCell = styled(FileHeaderCell)`
  width: 35%;
`

const FileRegisteredDatetimeCell = styled(FileHeaderCell)`
  width: 15%;
`

const FileIconStyle = styled.div`
  font-size: 21px;
  line-height: 1;
  min-width: 21px;
  text-align: center;
  margin-right: 8px;
  color: ${Colors.AccentDark};
`

const FileNameStyle: StyledDiv = styled.div`
  overflow-wrap: anywhere;
`

const FileRegisteredUserText = styled.div`
  white-space: normal;
  padding: 5px 16px 5px 0;
  width: 100%;
`

const NowrapCell = styled(TableCell)`
  white-space: nowrap;
`

const ProposalHeaderCell = styled(TableCell)`
  &.MuiTableCell-root {
    padding-top: 4px;
    padding-bottom: 4px;
    font-size: 0.85em;
    color: ${Colors.AccentDark};
  }

  background-color: ${Colors.Background};
`

const ProposalNumberCell = styled(ProposalHeaderCell)`
  width: 90px;
`

const ProposalResolutionTypeCell = styled(ProposalHeaderCell)`
  width: 90px;
`

const ItemRow = styled.div`
  display: flex;
  align-items: flex-start;
`;

const ItemLabel = styled.span`
  white-space: nowrap;
`;

const ItemValue = styled.span`
  word-break: break-word;
  overflow-wrap: anywhere;
`;

const FooterArea = styled(RowFlexContainer)`
  justify-content: space-between;
  margin-top: 26px
`

const NoticeContentArea = styled.div`
  padding: 16px 16px 24px 16px;
  word-break: break-word;
  overflow-wrap: anywhere;
  white-space: pre-wrap;
`

const StatusLabel = styled(LeftRowFlexContainer)`
  padding: 4px 8px;
  margin-left: 10px;
  gap: 8px;
  font-size: ${FontSizeInPixel.Body}px;
  background-color: ${Colors.Background};
  border-radius: 40px;
`

const StyledCancelIcon = styled(CancelIcon)`
  font-size: 16px;
`

const StyledLanguageIcon = styled(LanguageIcon)`
  font-size: 16px;
`

const StyledHowToVoteIcon = styled(HowToVoteIcon)`
  font-size: 16px;
`

const StyledTaskAltIcon = styled(TaskAltIcon)`
  font-size: 16px;
`

const StyledChatBubbleIcon = styled(ChatIcon)`
  font-size: 16px;
`

const PaperMainArea = styled(LeftColumnFlexContainer)`
  padding: 16px 16px 24px 16px;
`

const CancelDateTimeText = styled.div`
  padding-top: 20px;
`

const StatusMessageText = styled(FullWidthLeftRowFlexContainer)<{ isDraft: boolean }>`
  padding: 16px;
  margin-bottom: 16px;
  font-size: ${FontSizeInPixel.Body}px;
  font-weight: ${FontWeights.Bold};
  background-color: ${({isDraft}) => isDraft
      ? Colors.AdminWarningAlertLight
      : Colors.AdminInfoAlertLight
  };
  border-radius: 4px;
  white-space: nowrap;
`

const StyledReportProblemOutlinedIcon = styled(ReportProblemOutlinedIcon)`
  font-size: 22px;
  margin-right: 12px;
  color: ${Colors.AdminWarningAlertDark};
`

const StyledInfoOutlinedIcon = styled(InfoOutlined)`
  font-size: 22px;
  margin-right: 12px;
  color: ${Colors.AdminInfoAlertDark};
`

const VotingPreferenceCloseButton = styled(AdminPrimaryButton)`
  align-self: center;
  width: 236px;
  margin-bottom: 8px;
`

const VotingPreferenceReopenButton = styled(AdminPrimaryOutlinedButton)`
  align-self: center;
  width: 236px;
  margin-bottom: 8px;
`

const CautionText = styled(LeftTopRowFlexContainer)`
  font-size: ${FontSizeInPixel.Small}px;
  color: rgba(0, 0, 0, 0.70);
`

const AssocNameLabel = styled(FlexContainer)`
  width: 100%;
  justify-content: flex-start;
  padding-bottom: 8px;
  font-size: ${FontSizeInPixel.Body}px;
  word-break: break-word;
  overflow-wrap: anywhere;
`

const CountResultArea = styled(FullWidthColumnFlexContainer)`
  align-items: flex-start;
  gap: 8px;
  font-size: ${FontSizeInPixel.Body}px;
  padding-bottom: 8px;
`

const CountText = styled(RowFlexContainer)`
  height: 36px;
  gap: 8px;
`

const CountLabel = styled.div`
  font-weight: bold;
  font-size: ${FontSizeInPixel.Big}px;
`

const AdditionalNote = styled(FullWidthRightRowFlexContainer)`
  font-size: ${FontSizeInPixel.Small}px;
  color: rgba(0, 0, 0, 0.70);
`

const StyledDivider = styled(Divider)`
  width: 100%;
`
